<template>
  <div class="p-6">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Gestión de Proveedores</h1>
        <p class="text-gray-600 dark:text-gray-400">Administra los proveedores del sistema</p>
      </div>
      <div class="flex items-center gap-3">
        <button
          @click="openCreateModal"
          class="btn-primary"
        >
          <Icon icon="mdi:plus" />
          Nuevo Proveedor
        </button>
      </div>
    </div>

    <!-- Filtros Compactos -->
    <div class="premium-card p-4 mb-4">
      <div class="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div class="md:col-span-4">
          <label class="label-premium text-[10px]">Buscar Proveedor</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Nombre, RIF, contacto..." @input="handleSearch" class="input-premium pl-9 py-1.5 text-xs" />
          </div>
        </div>
        <div class="md:col-span-2">
          <label class="label-premium text-[10px]">Estado</label>
          <select v-model="filterStatus" @change="handleFilterChange" class="input-premium py-1.5 text-xs cursor-pointer">
            <option value="all">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="label-premium text-[10px]">Tipo</label>
          <select v-model="filterTipo" class="input-premium py-1.5 text-xs cursor-pointer">
            <option value="all">Cualquier Tipo</option>
            <option v-for="tipo in tiposProveedores" :key="tipo" :value="tipo">{{ tipo }}</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="label-premium text-[10px]">Ordenar Por</label>
          <select v-model="sortBy" class="input-premium py-1.5 text-xs cursor-pointer">
            <option value="nombre">Nombre (A-Z)</option>
            <option value="rif">RIF (A-Z)</option>
            <option value="totalProductos_desc">Más productos</option>
          </select>
        </div>
        <div class="md:col-span-2">
           <button @click="loadProveedores" class="w-full btn-primary py-1.5 text-xs">
             <Icon icon="mdi:refresh" :class="{ 'animate-spin': loading }" />
             <span>{{ loading ? 'Actualizando' : 'Actualizar' }}</span>
           </button>
        </div>
      </div>
    </div>

    <!-- Estadísticas Modernas -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-blue-500/10 rounded-xl">
          <Icon icon="mdi:truck-delivery" class="text-blue-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Total</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-none">{{ proveedores.length }}</p>
        </div>
      </div>

      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-green-500/10 rounded-xl">
          <Icon icon="mdi:check-circle" class="text-green-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Activos</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-none">{{ estadisticas.activos }}</p>
        </div>
      </div>

      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-red-500/10 rounded-xl">
          <Icon icon="mdi:close-circle" class="text-red-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Inactivos</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-none">{{ estadisticas.inactivos }}</p>
        </div>
      </div>

      <div class="premium-card p-4 flex items-center gap-4">
        <div class="p-3 bg-purple-500/10 rounded-xl">
          <Icon icon="mdi:package-variant" class="text-purple-500 text-2xl" />
        </div>
        <div>
          <p class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Promedio Ítems</p>
          <p class="text-xl font-black text-gray-800 dark:text-white leading-none">{{ estadisticas.promedioProductos }}</p>
        </div>
      </div>
    </div>

    <!-- Indicadores de filtro -->
    <div v-if="isFiltered" class="mb-4">
      <div class="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
        <span class="text-sm font-medium text-amber-800 dark:text-amber-300">
          <Icon icon="mdi:filter" class="inline mr-1" />
          Filtros aplicados:
        </span>
        <span v-if="searchQuery" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
          Buscar: "{{ searchQuery }}"
          <button @click="searchQuery = ''" class="ml-1.5 hover:text-blue-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterStatus !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
          Estado: {{ filterStatus === 'activos' ? 'Activos' : 'Inactivos' }}
          <button @click="filterStatus = 'all'; handleFilterChange()" class="ml-1.5 hover:text-indigo-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterTipo !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-800 dark:text-teal-200">
          Tipo: {{ filterTipo }}
          <button @click="filterTipo = 'all'" class="ml-1.5 hover:text-teal-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <button
          @click="clearFilters"
          class="text-sm text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-medium"
        >
          <Icon icon="mdi:filter-off" class="inline mr-1" />
          Limpiar filtros
        </button>
      </div>
    </div>

    <!-- Tabla de proveedores -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <!-- Encabezado de tabla -->
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Lista de Proveedores</h2>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Mostrando {{ filteredProveedores.length }} de {{ proveedores.length }} proveedores
          </p>
        </div>
        <div class="flex items-center space-x-2">
          <div class="text-sm text-gray-500 dark:text-gray-400">
            <Icon icon="mdi:calendar" class="inline mr-1" />
            Última actualización: {{ formatTime(lastUpdate) }}
          </div>
        </div>
      </div>

      <!-- Tabla -->
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Proveedor</span>
                  <button @click="sortBy = sortBy === 'nombre' ? 'nombre_desc' : 'nombre'">
                    <Icon icon="mdi:sort-alphabetical" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Contacto
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Tipo / Especialidad
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Productos / Compras
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Estado
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <!-- Estado de carga -->
            <tr v-if="loading">
              <td colspan="6" class="px-6 py-12">
                <div class="flex flex-col items-center justify-center">
                  <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                  <p class="text-gray-600 dark:text-gray-400">Cargando proveedores...</p>
                </div>
              </td>
            </tr>

            <!-- Sin resultados -->
            <tr v-else-if="filteredProveedores.length === 0">
              <td colspan="6" class="px-6 py-16 text-center">
                <div class="flex flex-col items-center">
                  <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <Icon icon="mdi:truck-remove" class="text-3xl text-gray-400" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No se encontraron proveedores
                  </h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                    {{ searchQuery || filterStatus !== 'all' || filterTipo !== 'all'
                      ? 'Intenta con otros términos de búsqueda o cambia los filtros.' 
                      : 'No hay proveedores registrados. ¡Crea el primero!' }}
                  </p>
                  <button
                    v-if="!searchQuery && filterStatus === 'all' && filterTipo === 'all'"
                    @click="openCreateModal"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Icon icon="mdi:plus" class="mr-2 inline" />
                    Crear primer proveedor
                  </button>
                  <button
                    v-else
                    @click="clearFilters"
                    class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Icon icon="mdi:filter-off" class="mr-2 inline" />
                    Limpiar filtros
                  </button>
                </div>
              </td>
            </tr>

            <!-- Lista de proveedores -->
            <tr 
              v-else 
              v-for="proveedor in paginatedProveedores" 
              :key="proveedor.idProveedor"
              class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 cursor-pointer"
              @click="openDetailModal(proveedor)"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-12 w-12">
                    <div :class="[
                      'h-12 w-12 rounded-xl flex items-center justify-center shadow-sm',
                      proveedor.estado?.toLowerCase() === 'activo'
                        ? 'bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900'
                    ]">
                      <Icon 
                        icon="mdi:truck-delivery" 
                        :class="[
                          'text-xl',
                          proveedor.estado?.toLowerCase() === 'activo'
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-500 dark:text-gray-400'
                        ]" 
                      />
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-semibold text-gray-900 dark:text-white">
                      {{ proveedor.nombre }}
                      <span v-if="proveedor.estado?.toLowerCase() !== 'activo'" class="ml-2 text-xs text-gray-500">
                        (Inactivo)
                      </span>
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 flex items-center mt-1">
                      <Icon icon="mdi:identifier" class="mr-1" />
                      ID: {{ proveedor.idProveedor }} | RIF: {{ proveedor.rif }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="space-y-1">
                  <div class="flex items-center text-sm text-gray-900 dark:text-white">
                    <Icon icon="mdi:account" class="mr-2 text-gray-400" />
                    {{ proveedor.contacto || 'Sin contacto' }}
                  </div>
                  <div class="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <Icon icon="mdi:phone" class="mr-2" />
                    {{ proveedor.telefono || 'Sin teléfono' }}
                  </div>
                  <div v-if="proveedor.email" class="flex items-center text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                    <Icon icon="mdi:email" class="mr-2" />
                    {{ proveedor.email }}
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="space-y-1">
                  <span v-if="proveedor.tipoProveedor" 
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
                    {{ proveedor.tipoProveedor }}
                  </span>
                  <p class="text-sm text-gray-900 dark:text-white truncate max-w-xs" :title="proveedor.especialidad">
                    {{ proveedor.especialidad || 'Sin especialidad' }}
                  </p>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex space-x-3">
                  <div class="text-center">
                    <div class="text-lg font-semibold text-blue-600 dark:text-blue-400">
                      {{ proveedor.totalProductos || 0 }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Productos</div>
                  </div>
                  <div class="text-center">
                    <div class="text-lg font-semibold text-green-600 dark:text-green-400">
                      {{ proveedor.totalCompras || 0 }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Compras</div>
                  </div>
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {{ formatDate(proveedor.fechaRegistro) }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  @click.stop="toggleStatus(proveedor)"
                  :class="[
                    'px-3 py-1.5 inline-flex text-xs leading-4 font-semibold rounded-full shadow-sm transition-all duration-300 cursor-pointer',
                    proveedor.estado?.toLowerCase() === 'activo'
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900 text-green-800 dark:text-green-200 hover:from-green-200 hover:to-emerald-200 dark:hover:from-green-800 dark:hover:to-emerald-800'
                      : 'bg-gradient-to-r from-red-100 to-pink-100 dark:from-red-900 dark:to-pink-900 text-red-800 dark:text-red-200 hover:from-red-200 hover:to-pink-200 dark:hover:from-red-800 dark:hover:to-pink-800'
                  ]"
                >
                  <Icon :icon="proveedor.estado?.toLowerCase() === 'activo' ? 'mdi:check-circle' : 'mdi:close-circle'" class="mr-1.5" />
                  {{ proveedor.estado || 'Sin estado' }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex items-center space-x-2">
                  <button
                    @click.stop="openEditModal(proveedor)"
                    class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil" />
                  </button>
                  <button
                    @click.stop="toggleStatus(proveedor)"
                    :class="[
                      'p-2 rounded-lg transition-colors',
                      proveedor.estado?.toLowerCase() === 'activo'
                        ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-800/50'
                        : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800/50'
                    ]"
                    :title="proveedor.estado?.toLowerCase() === 'activo' ? 'Desactivar' : 'Activar'"
                  >
                    <Icon :icon="proveedor.estado?.toLowerCase() === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación Premium -->
      <div v-if="filteredProveedores.length > itemsPerPage" class="bg-gray-50/50 dark:bg-gray-800/50 px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-tighter">
          Mostrando {{ startItem }} - {{ Math.min(endItem, filteredProveedores.length) }} <span class="mx-1 text-gray-300">|</span> Total: {{ filteredProveedores.length }}
        </div>
        <div class="flex items-center gap-1">
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" :class="['p-1.5 rounded-lg border transition-all', currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-left" class="text-lg" />
          </button>
          
          <div class="flex items-center gap-1 mx-1">
            <button v-for="page in visiblePages" :key="page" @click="currentPage = page" :class="['w-8 h-8 rounded-lg text-xs font-bold transition-all flex items-center justify-center', currentPage === page ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600']">
              {{ page }}
            </button>
          </div>

          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" :class="['p-1.5 rounded-lg border transition-all', currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm']">
            <Icon icon="mdi:chevron-right" class="text-lg" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para crear/editar proveedor -->
    <BaseModal 
      v-model="showModal" 
      :title="editingProveedor ? 'Editar Proveedor' : 'Nuevo Proveedor'"
      :icon="editingProveedor ? 'mdi:pencil-box' : 'mdi:truck-plus'"
      iconType="primary"
    >
      <form @submit.prevent="saveProveedor" class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="label-premium">Empresa *</label>
            <input v-model="form.nombre" type="text" required maxlength="100" class="input-premium" placeholder="Nombre comercial" @blur="form.nombre = toTitleCase(form.nombre)" />
          </div>
          <div>
            <label class="label-premium">RIF *</label>
            <input v-model="form.rif" type="text" required maxlength="15" class="input-premium" placeholder="J123456789" />
          </div>
          <div>
            <label class="label-premium">Tipo</label>
            <select v-model="form.tipoProveedor" class="input-premium">
              <option value="">Seleccionar tipo</option>
              <option value="Mayorista">Mayorista</option>
              <option value="Distribuidor">Distribuidor</option>
              <option value="Fabricante">Fabricante</option>
            </select>
          </div>
          <div>
            <label class="label-premium">Contacto</label>
            <input v-model="form.contacto" type="text" maxlength="50" class="input-premium" placeholder="Nombre del asesor" @blur="form.contacto = toTitleCase(form.contacto)" />
          </div>
          <div>
            <label class="label-premium">Teléfono</label>
            <input v-model="form.telefono" type="tel" maxlength="20" class="input-premium" placeholder="0412-0000000" />
          </div>
          <div>
            <label class="label-premium">Email</label>
            <input v-model="form.email" type="email" maxlength="100" class="input-premium" placeholder="ventas@ejemplo.com" />
          </div>
        </div>
        <div>
          <label class="label-premium">Dirección</label>
          <textarea v-model="form.direccion" rows="2" class="input-premium resize-none" placeholder="Ubicación física" @blur="form.direccion = toTitleCase(form.direccion)"></textarea>
        </div>
        <div class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-dashed border-gray-300 dark:border-gray-600">
          <label class="text-[10px] font-bold text-gray-400 uppercase">Estado:</label>
          <div class="flex bg-white dark:bg-gray-800 p-1 rounded-lg shadow-inner gap-1">
            <button type="button" @click="form.estado = 'activo'" :class="['px-4 py-1.5 rounded-md text-[10px] font-bold uppercase transition-all', form.estado === 'activo' ? 'bg-green-500 text-white shadow-md' : 'text-gray-400']">Activo</button>
            <button type="button" @click="form.estado = 'inactivo'" :class="['px-4 py-1.5 rounded-md text-[10px] font-bold uppercase transition-all', form.estado === 'inactivo' ? 'bg-red-500 text-white shadow-md' : 'text-gray-400']">Inactivo</button>
          </div>
        </div>
        
        <div class="flex gap-3 pt-4">
          <button type="button" @click="closeModal" class="flex-1 btn-secondary">Cancelar</button>
          <button type="submit" :disabled="saving || !isFormValid" class="flex-1 btn-primary">
            <Icon v-if="saving" icon="mdi:loading" class="animate-spin" />
            {{ editingProveedor ? 'Actualizar' : 'Crear' }}
          </button>
        </div>
      </form>
    </BaseModal>

    <!-- Modal de detalles -->
    <BaseModal 
      v-model="showDetailModal" 
      title="Perfil del Proveedor"
      icon="mdi:truck-delivery"
      iconType="primary"
      maxWidth="max-w-md"
    >
      <div v-if="selectedProveedor" class="space-y-4">
        <div class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-center">
          <h4 class="text-lg font-bold text-gray-900 dark:text-white">{{ selectedProveedor.nombre }}</h4>
          <p class="text-xs text-gray-500 uppercase tracking-widest mt-1">RIF: {{ selectedProveedor.rif }}</p>
        </div>
        
        <div class="grid grid-cols-2 gap-3 text-center">
          <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <label class="text-[10px] font-bold text-gray-400 uppercase">Estado</label>
            <p :class="['text-xs font-bold mt-1 uppercase', selectedProveedor.estado?.toLowerCase() === 'activo' ? 'text-green-500' : 'text-red-500']">{{ selectedProveedor.estado }}</p>
          </div>
          <div class="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <label class="text-[10px] font-bold text-gray-400 uppercase">Productos</label>
            <p class="text-xs font-bold text-blue-600 mt-1">{{ selectedProveedor.totalProductos || 0 }} ítems</p>
          </div>
        </div>

        <div class="space-y-3">
          <div class="flex items-center gap-3 p-2">
            <Icon icon="mdi:account" class="text-gray-400" />
            <span class="text-sm text-gray-600 dark:text-gray-300">{{ selectedProveedor.contacto || 'Sin contacto' }}</span>
          </div>
          <div class="flex items-center gap-3 p-2">
            <Icon icon="mdi:phone" class="text-gray-400" />
            <span class="text-sm text-gray-600 dark:text-gray-300">{{ selectedProveedor.telefono || 'Sin teléfono' }}</span>
          </div>
          <div v-if="selectedProveedor.email" class="flex items-center gap-3 p-2">
            <Icon icon="mdi:email" class="text-gray-400" />
            <span class="text-sm text-gray-600 dark:text-gray-300">{{ selectedProveedor.email }}</span>
          </div>
        </div>

        <div class="flex gap-3 pt-4">
          <button @click="openEditModal(selectedProveedor); closeDetailModal()" class="flex-1 btn-primary">Editar</button>
          <button @click="closeDetailModal" class="flex-1 btn-secondary">Cerrar</button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { useModalStore } from "@/store/modal";
import BaseModal from "@/components/common/BaseModal.vue";
import { debounce } from 'lodash';
import { toTitleCase } from '@/utils/stringUtils.js';
import { notificarProveedor } from '@/utils/notificacionesHelper';

// Importar funciones de la API de proveedores
import {
  obtenerProveedores,
  crearProveedor,
  actualizarProveedor,
  cambiarEstadoProveedor,
  verificarRifProveedor,
  // Funciones auxiliares
  buscarProveedores,
  filtrarProveedoresPorEstado,
  filtrarProveedoresPorTipo,
  obtenerEstadisticasProveedores,
  obtenerTiposUnicosProveedores
} from '@/api/proveedores.js';

// Importar funciones de reportes
import {
  cargarLogo,
  imprimirReporteProveedores,
  imprimirReporteProveedoresSimplificado
} from '@/utils/reporteProveedoresGenerator.js';

export default {
  name: 'ProveedoresList',
  components: {
    Icon,
    BaseModal
  },
  setup() {
    const toast = useToast();
    const modal = useModalStore();
    return { toast, modal };
  },
  data() {
    return {
      proveedores: [],
      loading: false,
      saving: false,
      showModal: false,
      showReportMenu: false,
      searchQuery: '',
      filterStatus: 'all',
      filterTipo: 'all',
      sortBy: 'nombre',
      currentPage: 1,
      itemsPerPage: 10,
      form: {
        idProveedor: null,
        nombre: '',
        rif: '',
        telefono: '',
        email: '',
        direccion: '',
        contacto: '',
        tipoProveedor: '',
        especialidad: '',
        estado: 'activo',
      },
      errors: {},
      editingProveedor: null,
      selectedProveedor: null,
      showDetailModal: false,
      lastUpdate: new Date(),
      isProcessing: false, // Bandera para evitar múltiples clics
    };
  },
  computed: {
    filteredProveedores() {
      let filtered = [...this.proveedores];
      
      // Aplicar búsqueda
      if (this.searchQuery.trim()) {
        filtered = buscarProveedores(filtered, this.searchQuery);
      }
      
      // Aplicar filtro de estado
      filtered = filtrarProveedoresPorEstado(filtered, this.filterStatus);
      
      // Aplicar filtro de tipo
      filtered = filtrarProveedoresPorTipo(filtered, this.filterTipo);
      
      // Aplicar ordenamiento
      filtered.sort((a, b) => {
        switch (this.sortBy) {
          case 'nombre':
            return (a.nombre || '').localeCompare(b.nombre || '');
          case 'nombre_desc':
            return (b.nombre || '').localeCompare(a.nombre || '');
          case 'rif':
            return (a.rif || '').localeCompare(b.rif || '');
          case 'rif_desc':
            return (b.rif || '').localeCompare(a.rif || '');
          case 'fechaRegistro_desc':
            return new Date(b.fechaRegistro || 0) - new Date(a.fechaRegistro || 0);
          case 'fechaRegistro':
            return new Date(a.fechaRegistro || 0) - new Date(b.fechaRegistro || 0);
          case 'totalProductos_desc':
            return (b.totalProductos || 0) - (a.totalProductos || 0);
          case 'totalCompras_desc':
            return (b.totalCompras || 0) - (a.totalCompras || 0);
          default:
            return 0;
        }
      });
      
      return filtered;
    },
    
    estadisticas() {
      return obtenerEstadisticasProveedores(this.proveedores);
    },
    
    tiposProveedores() {
      return obtenerTiposUnicosProveedores(this.proveedores);
    },
    
    paginatedProveedores() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProveedores.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredProveedores.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredProveedores.length);
    },
    
    isFormValid() {
      return this.form.nombre.trim() && this.form.rif.trim();
    },
    
    isFiltered() {
      return this.searchQuery.trim() || this.filterStatus !== 'all' || this.filterTipo !== 'all';
    },
  },
  mounted() {
    this.loadProveedores();
    // Precargar el logo para los reportes
    this.cargarLogoReportes();
  },
  watch: {
    filterStatus() {
      this.currentPage = 1;
    },
    filterTipo() {
      this.currentPage = 1;
    },
    searchQuery() {
      this.currentPage = 1;
    },
    sortBy() {
      this.currentPage = 1;
    },
  },
  methods: {
    toTitleCase,
    async cargarLogoReportes() {
      try {
        await cargarLogo();
        console.log('✅ Logo cargado para reportes');
      } catch (error) {
        console.error('❌ Error cargando logo:', error);
      }
    },

    async loadProveedores() {
      this.loading = true;
      try {
        const response = await obtenerProveedores(this.filterStatus);
        
        if (response.success) {
          this.proveedores = response.data || [];
          this.lastUpdate = new Date();
          console.log('✅ Proveedores cargados:', this.proveedores.length);
        } else {
          throw new Error(response.error || 'Error al cargar proveedores');
        }
      } catch (error) {
        console.error('❌ Error al cargar proveedores:', error);
        this.showErrorAlert('Error al cargar los proveedores: ' + error.message);
      } finally {
        this.loading = false;
      }
    },
    
    async imprimirReporteCompleto() {
      if (this.filteredProveedores.length === 0) {
        this.showWarningAlert('No hay proveedores para generar el reporte');
        return;
      }

      try {
        await cargarLogo();
        const resultado = await imprimirReporteProveedores(
          this.filteredProveedores,
          'REPORTE GENERAL DE PROVEEDORES',
          {
            estado: this.filterStatus,
            tipoProveedor: this.filterTipo,
            busqueda: this.searchQuery
          }
        );
        
        if (!resultado) {
          this.showWarningAlert('No se pudo generar el reporte');
        }
        
        this.showReportMenu = false;
      } catch (error) {
        console.error('❌ Error generando reporte:', error);
        this.showErrorAlert('Error al generar el reporte: ' + error.message);
      }
    },
    
    async imprimirReporteSimplificado() {
      if (this.filteredProveedores.length === 0) {
        this.showWarningAlert('No hay proveedores para generar el listado');
        return;
      }

      try {
        await cargarLogo();
        const resultado = await imprimirReporteProveedoresSimplificado(
          this.filteredProveedores,
          'LISTADO DE PROVEEDORES'
        );
        
        if (!resultado) {
          this.toast.warning('No se pudo generar el listado');
        }
        
        this.showReportMenu = false;
      } catch (error) {
        console.error('❌ Error generando listado:', error);
        this.toast.error('Error al generar el listado: ' + error.message);
      }
    },
    
    handleFilterChange() {
      this.currentPage = 1;
      this.loadProveedores();
    },
    
    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300),
    
    openCreateModal() {
      this.editingProveedor = null;
      this.resetForm();
      this.errors = {};
      this.showModal = true;
      this.showReportMenu = false;
    },
    
    openEditModal(proveedor) {
      this.editingProveedor = proveedor;
      this.form = {
        idProveedor: proveedor.idProveedor,
        nombre: proveedor.nombre,
        rif: proveedor.rif,
        telefono: proveedor.telefono || '',
        email: proveedor.email || '',
        direccion: proveedor.direccion || '',
        contacto: proveedor.contacto || '',
        tipoProveedor: proveedor.tipoProveedor || '',
        especialidad: proveedor.especialidad || '',
        estado: proveedor.estado || 'activo',
      };
      this.errors = {};
      this.showModal = true;
      this.showReportMenu = false;
    },
    
    openDetailModal(proveedor) {
      this.selectedProveedor = proveedor;
      this.showDetailModal = true;
      this.showReportMenu = false;
    },
    
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedProveedor = null;
    },
    
    showDetailModalWithSwal(proveedor) {
      const isActivo = proveedor.estado?.toLowerCase() === 'activo';
      
      Swal.fire({
        title: 'Detalles del Proveedor',
        html: `
          <div class="text-left space-y-4">
            <div class="flex items-center space-x-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 rounded-lg">
              <div class="p-3 rounded-lg ${isActivo ? 'bg-green-500' : 'bg-red-500'}">
                <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div class="text-left">
                <h3 class="text-xl font-bold text-gray-900 dark:text-white">${proveedor.nombre}</h3>
                <p class="text-sm text-gray-600 dark:text-gray-300">ID: ${proveedor.idProveedor} | RIF: ${proveedor.rif}</p>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Tipo</p>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">${proveedor.tipoProveedor || 'No especificado'}</p>
              </div>
              <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Especialidad</p>
                <p class="text-sm font-semibold text-gray-900 dark:text-white">${proveedor.especialidad || 'No especificada'}</p>
              </div>
            </div>

            <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Contacto</p>
              <div class="space-y-2">
                <div class="flex items-center text-sm">
                  <svg class="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0 2c-1.66 0-5 1.08-5 3v1h10v-1c0-1.92-3.34-3-5-3z"/>
                  </svg>
                  <span class="text-gray-900 dark:text-white">${proveedor.contacto || 'Sin contacto'}</span>
                </div>
                <div class="flex items-center text-sm">
                  <svg class="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                  </svg>
                  <span class="text-gray-900 dark:text-white">${proveedor.telefono || 'Sin teléfono'}</span>
                </div>
                ${proveedor.email ? `
                <div class="flex items-center text-sm">
                  <svg class="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  <span class="text-gray-900 dark:text-white">${proveedor.email}</span>
                </div>
                ` : ''}
              </div>
            </div>

            ${proveedor.direccion ? `
            <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Dirección</p>
              <p class="text-sm text-gray-900 dark:text-white">${proveedor.direccion}</p>
            </div>
            ` : ''}

            <div class="grid grid-cols-3 gap-3">
              <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Estado</p>
                <span class="inline-block px-2 py-1 mt-1 text-xs font-semibold rounded-full ${isActivo ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}">
                  ${proveedor.estado || 'Sin estado'}
                </span>
              </div>
              <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Productos</p>
                <p class="text-xl font-bold text-blue-600 dark:text-blue-400">${proveedor.totalProductos || 0}</p>
              </div>
              <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
                <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Compras</p>
                <p class="text-xl font-bold text-green-600 dark:text-green-400">${proveedor.totalCompras || 0}</p>
              </div>
            </div>

            <div class="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400">Fecha de Registro</p>
              <div class="flex items-center justify-center mt-1">
                <svg class="w-4 h-4 mr-2 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                </svg>
                <span class="text-sm text-gray-900 dark:text-white">${this.formatDate(proveedor.fechaRegistro)}</span>
                <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">${this.formatTime(proveedor.fechaRegistro)}</span>
              </div>
            </div>
          </div>
        `,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Editar',
        cancelButtonText: 'Cerrar',
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#6b7280',
        reverseButtons: true,
        allowOutsideClick: true,
        allowEscapeKey: true,
        width: '32rem'
      }).then((result) => {
        if (result.isConfirmed) {
          this.openEditModal(proveedor);
        }
      });
    },
    
    closeModal() {
      this.showModal = false;
      this.resetForm();
      this.errors = {};
    },
    
    resetForm() {
      this.form = {
        idProveedor: null,
        nombre: '',
        rif: '',
        telefono: '',
        email: '',
        direccion: '',
        contacto: '',
        tipoProveedor: '',
        especialidad: '',
        estado: 'activo',
      };
      this.editingProveedor = null;
    },
    
    validateForm() {
      this.errors = {};
      
      if (!this.form.nombre.trim()) {
        this.errors.nombre = 'El nombre es requerido';
      } else if (this.form.nombre.length > 100) {
        this.errors.nombre = 'El nombre no puede exceder 100 caracteres';
      }
      
      if (!this.form.rif.trim()) {
        this.errors.rif = 'El RIF es requerido';
      } else if (this.form.rif.length > 15) {
        this.errors.rif = 'El RIF no puede exceder 15 caracteres';
      }
      
      if (this.form.email && !this.isValidEmail(this.form.email)) {
        this.errors.email = 'El email no es válido';
      } else if (this.form.email.length > 100) {
        this.errors.email = 'El email no puede exceder 100 caracteres';
      }
      
      return Object.keys(this.errors).length === 0;
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    
    async saveProveedor() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      try {
        if (!this.editingProveedor) {
          const verifyResult = await verificarRifProveedor(this.form.rif);
          
          if (verifyResult.success && verifyResult.existe) {
            this.errors.rif = 'Este RIF ya está registrado';
            this.saving = false;
            return;
          }
        }

        const proveedorData = {
          nombre: this.form.nombre,
          rif: this.form.rif,
          telefono: this.form.telefono || null,
          email: this.form.email || null,
          direccion: this.form.direccion || null,
          contacto: this.form.contacto || null,
          tipoProveedor: this.form.tipoProveedor || null,
          especialidad: this.form.especialidad || null,
          estado: this.form.estado || 'activo'
        };

        let result;
        if (this.editingProveedor) {
          result = await actualizarProveedor(this.editingProveedor.idProveedor, proveedorData);
        } else {
          result = await crearProveedor(proveedorData);
        }

        if (result.success) {
          await this.loadProveedores();
          this.closeModal();
          this.toast.success(this.editingProveedor ? 'Proveedor actualizado correctamente' : 'Proveedor creado correctamente');
          notificarProveedor(
            this.editingProveedor ? 'Proveedor actualizado' : 'Nuevo proveedor registrado',
            this.form.nombre,
            `RIF: ${this.form.rif}`
          );
        } else {
          throw new Error(result.error || 'Error al guardar');
        }
      } catch (error) {
        console.error('❌ Error al guardar proveedor:', error);
        this.toast.error(error.message || 'Error al guardar el proveedor');
      } finally {
        this.saving = false;
      }
    },
    
    async toggleStatus(proveedor) {
      const esActivo = proveedor.estado?.toLowerCase() === 'activo';
      
      const confirmed = await this.modal.confirm({
        title: `¿${!esActivo ? 'Activar' : 'Desactivar'} proveedor?`,
        message: `¿Deseas ${!esActivo ? 'activar' : 'desactivar'} el proveedor <strong>"${proveedor.nombre}"</strong>?`,
        icon: 'mdi:help-circle',
        iconType: !esActivo ? 'success' : 'warning',
        confirmText: `Sí, ${!esActivo ? 'activar' : 'desactivar'}`,
        type: !esActivo ? 'success' : 'warning'
      });

      if (confirmed) {
        try {
          const nuevoEstado = !esActivo ? 'activo' : 'inactivo';
          const toggleResult = await cambiarEstadoProveedor(proveedor.idProveedor, nuevoEstado);
          
          if (toggleResult && toggleResult.success) {
            await this.loadProveedores();
            this.toast.success(`El proveedor ha sido ${!esActivo ? 'activado' : 'desactivado'} correctamente`);
            notificarProveedor(
              `Proveedor ${!esActivo ? 'activado' : 'desactivado'}`,
              proveedor.nombre,
              `RIF: ${proveedor.rif}`
            );
          } else {
            throw new Error(toggleResult?.error || 'Error al cambiar estado');
          }
        } catch (error) {
          console.error('❌ Error al cambiar estado:', error);
          this.toast.error(error.message || 'Error al cambiar el estado del proveedor');
        }
      }
    },
    
    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = 'all';
      this.filterTipo = 'all';
      this.sortBy = 'nombre';
      this.currentPage = 1;
      this.loadProveedores();
    },
    
    formatDate(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return fechaISO;
      }
    },
    
    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleTimeString('es-ES', {
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return '';
      }
    },
    
    showSuccessAlert(title, text) {
      Swal.fire({
        title,
        text,
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        position: 'top-end',
        toast: true,
        timerProgressBar: true,
        showCloseButton: true
      });
    },
    
    showErrorAlert(text) {
      Swal.fire({
        title: '¡Error!',
        text,
        icon: 'error',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#dc2626',
        allowOutsideClick: true,
        allowEscapeKey: true
      });
    },
    
    showWarningAlert(text) {
      Swal.fire({
        title: '¡Atención!',
        text,
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#f59e0b',
        allowOutsideClick: true,
        allowEscapeKey: true
      });
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-in {
  animation: fadeIn 0.3s ease-out;
}

::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.dark ::-webkit-scrollbar-track {
  background: #374151;
}

.dark ::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark ::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>